import { call, select, put } from "redux-saga/effects";
import { actions as apisNeedsActions } from "../reducers/apisNeeds";
import { actions as uiNeedsActions } from "../reducers/uiNeeds";
import { CQ_ACTION_CODES } from "../catalogConstants";

const uiNeedsStore = (state) => state.uiNeeds;
const apisNeedsStore = (state) => state.apiNeeds;


/**
 * Busca y devuelve el `cqsiId`, `id` y `productType` de una opción que coincide con el `actionId` y el `phoneNumber` proporcionados.
 * 
 * @param {string} actionId - El ID de la acción que se está buscando.
 * @param {string} phoneNumber - El número de teléfono del cliente al que se debe aplicar la acción.
 * @param {Array} options - Lista de opciones donde se realizará la búsqueda.
 * @returns {Object|null} - Retorna un objeto con `cqsiId`, `id` y `productType` si se encuentra una coincidencia, de lo contrario retorna `null`.
 */
const searchClientActionCqsiId = (actionId, phoneNumber, options) => {
  // Verifica si 'options' es un array
  if (Array.isArray(options)) {
    // Busca una opción que coincida tanto en actionId como en clientPhoneNumberToBeAppliedTo
    let option = options.find((opt) => {
      return (
        opt.actionId === actionId
        && opt.clientPhoneNumberToBeAppliedTo === phoneNumber
      );
    });
    if (option) {
      // Desestructura los valores de la opción encontrada
      const { cqsiId, id, productType } = option;
      return { cqsiId, id, productType };
    }
    // Si no se encuentra una coincidencia completa, busca por actionId solo
    option = options.find((opt) => {
      return opt.actionId === actionId;
    });
    if (option) {
      // Desestructura los valores de la opción encontrada
      const { cqsiId, id, productType } = option;
      return { cqsiId, id, productType };
    }
  }

  // Retorna null si no se encuentra ninguna opción que coincida
  return null;
};


/**
 * Busca y devuelve el `cqsiId` de una opción que coincide con el `actionId` y el `phoneNumber` 
 * dentro de un grupo de opciones especificado por `groupId`.
 * 
 * @param {string} actionId - El ID de la acción que se está buscando.
 * @param {string} phoneNumber - El número de teléfono del cliente al que se debe aplicar la acción.
 * @param {Array} optionsGroups - Lista de grupos de opciones donde se realizará la búsqueda.
 * @param {string} groupId - El ID del grupo de opciones en el cual se buscará.
 * @returns {string|null} - Retorna el `cqsiId` si se encuentra una coincidencia, de lo contrario retorna `null`.
 */
export const searchSecondaryClientActionCqsiId = (actionId, phoneNumber, optionsGroups, groupId) => {
  // Busca el grupo de opciones que coincide con el groupId proporcionado
  const optionsGroup = optionsGroups.find(group => group.id === groupId);

  // Si se encuentra el grupo de opciones
  if (optionsGroup) {
    // Busca la información de la opción dentro del grupo de opciones usando la función searchClientActionCqsiId
    const optionInfo = searchClientActionCqsiId(actionId, phoneNumber, optionsGroup.customerQuoteItems);

    // Si se encuentra la opción y contiene cqsiId, retorna cqsiId
    if (optionInfo?.cqsiId) {
      return optionInfo.cqsiId;
    }
  }

  // Retorna null si no se encuentra ninguna opción que coincida
  return null;
};


/**
 * Agrega una opción de migración a los grupos de opciones en el objeto de opciones secundarias.
 * 
 * Esta función realiza una copia profunda del objeto `secondaryOptionsObj` para evitar mutaciones del objeto original. Luego, busca y actualiza los grupos de opciones (`choiceGroups` y `keepGroups`) agregando una opción de migración o actualizándola si ya existe.
 * 
 * @param {Object} secondaryOptionsObj - Objeto que contiene las opciones secundarias a modificar.
 * @param {string} productType - Tipo de producto al que se aplican los cambios.
 * @param {number} groupIndex - Índice del grupo que no debe ser modificado.
 * @param {Object} previousSelection - Selección previa que contiene detalles para la opción de migración.
 * @param {Object} mainOptionSelected - Opción principal seleccionada que contiene los grupos de opciones a modificar.
 * @returns {Object} - Nuevo objeto `secondaryOptionsObj` con las modificaciones aplicadas.
 */
export const addMigrationOptionToElseOptions = (secondaryOptionsObj, productType, groupIndex, previousSelection, mainOptionSelected) => {
  // Realiza una copia profunda del objeto secondaryOptionsObj para evitar mutaciones en el objeto original
  const newSecondaryOptionsObj = JSON.parse(JSON.stringify(secondaryOptionsObj));

  // Verifica si existen grupos de opciones para el tipo de producto y opción principal seleccionada
  if (newSecondaryOptionsObj?.secondaryOptions?.[productType]?.[mainOptionSelected]?.choiceGroups?.length > 0) {
    const newChoiceGroups = [];

    // Itera sobre los grupos de opciones (choiceGroups) y actualiza/agrega la opción de migración
    newSecondaryOptionsObj.secondaryOptions[productType][mainOptionSelected].choiceGroups.forEach((choiceGroup, index) => {
      const newChoiceGroup = { ...choiceGroup };
      if (index !== groupIndex) {
        // Busca la opción de migración existente o crea una nueva si no se encuentra
        let newMigrationGroup = newChoiceGroup.customerQuoteItems.find(choice => choice.actionId === CQ_ACTION_CODES.MIGRATION);
        const newMigrationGroupIndex = newChoiceGroup.customerQuoteItems.indexOf(newMigrationGroup);

        if (!newMigrationGroup) {
          newMigrationGroup = {
            actionId: CQ_ACTION_CODES.MIGRATION,
            phoneOptions: [{
              label: previousSelection.phoneNumber,
              value: previousSelection.phoneNumber,
            }],
            description: newChoiceGroup.customerQuoteItems[0].description,
          };
          newChoiceGroup.customerQuoteItems.push(newMigrationGroup);
        } else {
          newMigrationGroup.phoneOptions.push({
            label: previousSelection.phoneNumber,
            value: previousSelection.phoneNumber,
          });
          const alreadySelectedValue = newSecondaryOptionsObj.secondaryOptionsSelected.find(
              (optionSelected) =>
                optionSelected.groupIndex === index && optionSelected.id === choiceGroup.id && optionSelected.actionId === CQ_ACTION_CODES.MIGRATION
            );
          if(alreadySelectedValue?.phoneNumber){
            newMigrationGroup.selected = alreadySelectedValue.phoneNumber;
          } else {
            newMigrationGroup.selected = null;
          }
          newChoiceGroup.customerQuoteItems[newMigrationGroupIndex] = newMigrationGroup;
        }
      }

      newChoiceGroups.push(newChoiceGroup);
    });

    // Actualiza los grupos de opciones (choiceGroups) en el nuevo objeto
    newSecondaryOptionsObj.secondaryOptions[productType][mainOptionSelected].choiceGroups = newChoiceGroups;
  }

  // Verifica si existen grupos de mantenimiento (keepGroups) para el tipo de producto y opción principal seleccionada
  if (newSecondaryOptionsObj?.secondaryOptions?.[productType]?.[mainOptionSelected]?.keepGroups?.length > 0) {
    const newKeepGroups = [];

    // Itera sobre los grupos de mantenimiento (keepGroups) y actualiza/agrega la opción de mantenimiento
    newSecondaryOptionsObj.secondaryOptions[productType][mainOptionSelected].keepGroups.forEach((keepGroup, index) => {
      const newKeepGroup = { ...keepGroup };
      if (index !== groupIndex) {
        // Busca la opción de mantenimiento existente o crea una nueva si no se encuentra
        let newKeepChoiceGroup = newKeepGroup.customerQuoteItems.find(choice => (choice.actionId === CQ_ACTION_CODES.MIGRATION || choice.actionId === CQ_ACTION_CODES.KEEP));
        const newKeepChoiceGroupIndex = newKeepGroup.customerQuoteItems.indexOf(newKeepChoiceGroup);

        if (!newKeepChoiceGroup) {
          newKeepChoiceGroup = {
            actionId: previousSelection.actionId,
            phoneOptions: [{
              label: previousSelection.phoneNumber,
              value: previousSelection.phoneNumber,
            }],
            description: previousSelection.description,
          };
          newKeepGroup.customerQuoteItems.push(newKeepChoiceGroup);
        } else {
          newKeepChoiceGroup.phoneOptions.push({
            label: previousSelection.phoneNumber,
            value: previousSelection.phoneNumber,
          });
          const alreadySelectedValue = newSecondaryOptionsObj.secondaryOptionsSelected.find(
            (optionSelected) =>
              optionSelected.groupIndex === index && optionSelected.id === keepGroup.id && (optionSelected.actionId === CQ_ACTION_CODES.MIGRATION || optionSelected.actionId === CQ_ACTION_CODES.KEEP)
          );
          if(alreadySelectedValue?.phoneNumber){
            newKeepChoiceGroup.selected = alreadySelectedValue.phoneNumber;
          }
          newKeepGroup.customerQuoteItems[newKeepChoiceGroupIndex] = newKeepChoiceGroup;
        }
      }

      newKeepGroups.push(newKeepGroup);
    });

    // Actualiza los grupos de mantenimiento (keepGroups) en el nuevo objeto
    newSecondaryOptionsObj.secondaryOptions[productType][mainOptionSelected].keepGroups = newKeepGroups;
  }

  // Retorna el nuevo objeto con las modificaciones aplicadas
  return newSecondaryOptionsObj;
};


/**
 * Elimina una opción de migración seleccionada de los grupos de opciones en el objeto de opciones secundarias.
 * 
 * Esta función realiza una copia profunda del objeto `secondaryOptionsObj` para evitar mutaciones del objeto original. Luego, busca y elimina la opción de migración seleccionada de los grupos de opciones (`choiceGroups`) y de los grupos de mantenimiento (`keepGroups`) basándose en el número de teléfono proporcionado.
 * 
 * @param {Object} secondaryOptionsObj - Objeto que contiene las opciones secundarias de las que se eliminará la opción de migración.
 * @param {string} productType - Tipo de producto al que se aplican los cambios.
 * @param {number} groupIndex - Índice del grupo que no debe ser modificado.
 * @param {string} phoneNumber - Número de teléfono de la opción de migración que se desea eliminar.
 * @param {Object} mainOptionSelected - Opción principal seleccionada que contiene los grupos de opciones a modificar.
 * @returns {Object} - Nuevo objeto `secondaryOptionsObj` con las modificaciones aplicadas.
 */
export const removeMigrationOptionsSelected = (secondaryOptionsObj, productType, groupIndex, phoneNumber, mainOptionSelected) => {
  // Realiza una copia profunda del objeto secondaryOptionsObj para evitar mutaciones en el objeto original
  const newSecondaryOptionsObj = JSON.parse(JSON.stringify(secondaryOptionsObj));

  // Verifica si existen grupos de opciones (choiceGroups) para el tipo de producto y opción principal seleccionada
  if (newSecondaryOptionsObj
    && newSecondaryOptionsObj.secondaryOptions
    && newSecondaryOptionsObj.secondaryOptions[productType]
    && newSecondaryOptionsObj.secondaryOptions[productType][mainOptionSelected]
    && newSecondaryOptionsObj.secondaryOptions[productType][mainOptionSelected].choiceGroups) {
    
    const newChoiceGroups = [];

    // Itera sobre los grupos de opciones (choiceGroups) y elimina la opción de migración seleccionada
    newSecondaryOptionsObj.secondaryOptions[productType][mainOptionSelected].choiceGroups.forEach((choiceGroup, index) => {
      const newChoiceGroup = { ...choiceGroup };
      if (index !== groupIndex) {
        // Busca la opción de migración en los elementos de cotización del cliente
        const migrationChoice = newChoiceGroup.customerQuoteItems.find(choice => choice.actionId === CQ_ACTION_CODES.MIGRATION);

        if (migrationChoice && migrationChoice.phoneOptions) {
          // Encuentra la opción de teléfono que debe ser eliminada
          const migrationPhoneToRemove = migrationChoice.phoneOptions.find(phone => phone.value === phoneNumber);

          if (migrationPhoneToRemove) {
            // Elimina la opción de teléfono de la lista de opciones
            const migrationPhoneToRemoveIndex = migrationChoice.phoneOptions.indexOf(migrationPhoneToRemove);
            migrationChoice.phoneOptions.splice(migrationPhoneToRemoveIndex, 1);
            
            // Si no quedan más opciones de teléfono, elimina la opción de migración
            if (migrationChoice.phoneOptions.length === 0) {
              const migrationChoiceToRemoveIndex = newChoiceGroup.customerQuoteItems.indexOf(migrationChoice);
              newChoiceGroup.customerQuoteItems.splice(migrationChoiceToRemoveIndex, 1);
            } else if (newSecondaryOptionsObj.secondaryOptionsSelected && migrationChoice.phoneOptions.length === 1) {
              // Actualiza la selección secundaria si es necesario
              const optionSelected = newSecondaryOptionsObj.secondaryOptionsSelected.find(optionSelected => optionSelected.actionId === CQ_ACTION_CODES.MIGRATION && optionSelected.groupIndex === index && !optionSelected.phoneNumber);
              if (optionSelected && migrationChoice.phoneOptions[0].value) {
                optionSelected.phoneNumber = migrationChoice.phoneOptions[0].value;
              }
            }
          }
        }
      }

      newChoiceGroups.push(newChoiceGroup);
    });

    // Actualiza los grupos de opciones (choiceGroups) en el nuevo objeto
    newSecondaryOptionsObj.secondaryOptions[productType][mainOptionSelected].choiceGroups = newChoiceGroups;
  }

  // Verifica si existen grupos de mantenimiento (keepGroups) para el tipo de producto y opción principal seleccionada
  if (newSecondaryOptionsObj
    && newSecondaryOptionsObj.secondaryOptions
    && newSecondaryOptionsObj.secondaryOptions[productType]
    && newSecondaryOptionsObj.secondaryOptions[productType][mainOptionSelected]
    && newSecondaryOptionsObj.secondaryOptions[productType][mainOptionSelected].keepGroups) {
    
    const newKeepGroups = [];

    // Itera sobre los grupos de mantenimiento (keepGroups) y elimina la opción de migración seleccionada
    newSecondaryOptionsObj.secondaryOptions[productType][mainOptionSelected].keepGroups.forEach((keepGroup, index) => {
      const newKeepGroup = { ...keepGroup };
      if (index !== groupIndex) {
        // Busca la opción de migración o mantenimiento en los elementos de cotización del cliente
        const keepChoice = newKeepGroup.customerQuoteItems.find(choice => (choice.actionId === CQ_ACTION_CODES.MIGRATION || choice.actionId === CQ_ACTION_CODES.KEEP));

        if (keepChoice) {
          // Encuentra la opción de teléfono que debe ser eliminada
          const choicePhoneToRemove = keepChoice.phoneOptions.find(phone => phone.value === phoneNumber);

          if (choicePhoneToRemove) {
            // Elimina la opción de teléfono de la lista de opciones
            const choicePhoneToRemoveIndex = keepChoice.phoneOptions.indexOf(choicePhoneToRemove);
            keepChoice.phoneOptions.splice(choicePhoneToRemoveIndex, 1);
            
            // Actualiza la selección secundaria si es necesario
            if (newSecondaryOptionsObj.secondaryOptionsSelected && keepChoice.phoneOptions.length === 1) {
              const optionSelected = newSecondaryOptionsObj.secondaryOptionsSelected.find(optionSelected => (optionSelected.actionId === CQ_ACTION_CODES.MIGRATION || optionSelected.actionId === CQ_ACTION_CODES.KEEP) && optionSelected.groupIndex === index && !optionSelected.phoneNumber);
              if (keepChoice.phoneOptions[0].value) {
                optionSelected.phoneNumber = keepChoice.phoneOptions[0].value;
              }
            }
          }
        }
      }

      newKeepGroups.push(newKeepGroup);
    });

    // Actualiza los grupos de mantenimiento (keepGroups) en el nuevo objeto
    newSecondaryOptionsObj.secondaryOptions[productType][mainOptionSelected].keepGroups = newKeepGroups;
  }

  // Retorna el nuevo objeto con las modificaciones aplicadas
  return newSecondaryOptionsObj;
};


/**
 * Función generadora que maneja la configuración de una acción principal basada en los parámetros proporcionados.
 * 
 * Esta función realiza las siguientes acciones:
 * 1. Obtiene la información de la tienda de `apisNeeds` del estado de la aplicación.
 * 2. Busca información sobre la acción a partir del `actionId` proporcionado.
 * 3. Si la acción es válida (es decir, `actionId` no es igual a `CQ_ACTION_CODES.NO_ACTION`), guarda la información de la acción en la tienda de `apisNeeds` y también actualiza la información en la interfaz de usuario.
 * 4. Si la acción no es válida, limpia la información de la acción en ambas tiendas (UI y `apisNeeds`).
 * 
 * @param {Object} params - Parámetros de entrada de la función generadora.
 * @param {Object} params.payload - Datos necesarios para configurar la acción principal.
 * @param {string} params.payload.actionId - ID de la acción que se está configurando.
 * @param {string} params.payload.description - Descripción asociada a la acción.
 * @returns {null} - La función no retorna ningún valor significativo.
 */
export function* setMainAction(params) {
  // Obtiene el estado de `apisNeeds` del store
  const apisNeeds = yield select(apisNeedsStore);
  const { selectElementsOptions } = apisNeeds;
  const { actionId, description, productOfferingName, phoneNumber } = params.payload;

  // Verifica si `actionId` es válido y si hay opciones de elementos seleccionados
  if (actionId !== CQ_ACTION_CODES.NO_ACTION && selectElementsOptions) {
    // Busca el cqsiId y el tipo de producto correspondiente a la acción
    const { cqsiId, productType } = searchClientActionCqsiId(actionId, null, selectElementsOptions);

    // Si se encuentra un cqsiId, guarda la información de la acción en el store de `apisNeeds`
    if (cqsiId) {
      const actionInfo = {
        actionId,
        cqsiId,
        productType,
        productOfferingName,
        description,
        selectedByClient: true,
        phoneNumber: phoneNumber || null,
      };
      
      yield put(apisNeedsActions.saveActionInfoForApi(actionInfo));
    }

    // Actualiza la información de la acción principal en la interfaz de usuario
    yield put(uiNeedsActions.saveMainActionInfoForUI({ actionId }));
  } else {
    // Si `actionId` no es válido, limpia la información de la acción en la interfaz de usuario y en el store de `apisNeeds`
    yield put(uiNeedsActions.saveMainActionInfoForUI(null));
    yield put(apisNeedsActions.saveActionInfoForApi({}));
  }

  // Retorna null, ya que la función no necesita retornar ningún valor significativo
  return null;
}



/**
 * Función generadora que actualiza la información adicional para una acción principal basada en los parámetros proporcionados.
 * 
 * Esta función realiza las siguientes acciones:
 * 1. Obtiene la información de la tienda de `apisNeeds` del estado de la aplicación.
 * 2. Extrae los parámetros necesarios (`phoneNumber` e `iccNumber`) del payload.
 * 3. Obtiene la información de la acción principal (`actionId` y `description`) desde `selectElementsNeeds`.
 * 4. Si la acción principal (`actionId`) y las opciones de elementos seleccionados (`selectElementsOptions`) están disponibles, busca el `cqsiId` y el tipo de producto correspondiente a la acción.
 * 5. Si se encuentra el `cqsiId`, crea un objeto de información de acción que incluye todos los detalles y actualiza la tienda de `apisNeeds` y la interfaz de usuario con la nueva información.
 * 
 * @param {Object} param - Parámetros de entrada de la función generadora.
 * @param {Object} param.payload - Datos necesarios para actualizar la acción principal.
 * @param {string} param.payload.phoneNumber - Número de teléfono asociado a la acción.
 * @param {string} param.payload.iccNumber - Número ICC asociado a la acción.
 * @returns {void} - La función no retorna ningún valor significativo.
 */
export function* setMainActionAdditionalInfo(param) {
  // Obtiene el estado de `apisNeeds` del store
  const apisNeeds = yield select(apisNeedsStore);
  const { selectElementsNeeds, selectElementsOptions } = apisNeeds;
  const { phoneNumber, iccNumber } = param.payload;
  const { actionId, description, productOfferingName } = selectElementsNeeds;

  // Verifica si `actionId` y `selectElementsOptions` están disponibles
  if (actionId && selectElementsOptions) {
    // Busca el cqsiId y el tipo de producto correspondiente a la acción
    const { cqsiId, productType } = searchClientActionCqsiId(actionId, phoneNumber, selectElementsOptions);

    // Si se encuentra un cqsiId, guarda la información de la acción en el store de `apisNeeds`
    if (cqsiId) {
      const actionInfo = {
        actionId,
        cqsiId,
        phoneNumber,
        productType,
        productOfferingName,
        description,
        iccNumber,
        selectedByClient: true,
      };

      // Actualiza la tienda de `apisNeeds` con la nueva información de la acción
      yield put(apisNeedsActions.saveActionInfoForApi(actionInfo));
      
      // Actualiza la información de la acción principal en la interfaz de usuario
      yield put(uiNeedsActions.saveMainActionInfoForUI({ actionId, phoneNumber, iccNumber }));
    }
  }
}



/**
 * Función generadora que actualiza la información de una acción secundaria en la interfaz de usuario.
 * 
 * Esta función realiza las siguientes acciones:
 * 1. Obtiene el estado de la tienda `uiNeeds` del store.
 * 2. Extrae los objetos de opciones secundarias (`secondaryOptionsObj`) y opciones principales (`mainOptionsObj`) del estado.
 * 3. Crea una copia profunda del objeto de opciones secundarias para evitar mutaciones directas.
 * 4. Busca la selección secundaria previa en el objeto de opciones secundarias seleccionadas (`secondaryOptionsSelected`).
 * 5. Actualiza o elimina la selección secundaria en función del `actionId` proporcionado.
 * 6. Si la acción previa era una acción de migración, actualiza las opciones secundarias con la nueva opción de migración.
 * 7. Actualiza la tienda de `uiNeeds` con la nueva información de las opciones secundarias.
 * 
 * @param {Object} params - Parámetros de entrada para actualizar la acción secundaria.
 * @param {Object} params.payload - Datos necesarios para actualizar la acción secundaria.
 * @param {string} params.payload.actionId - ID de la acción secundaria a configurar.
 * @param {string} params.payload.productType - Tipo de producto asociado a la acción.
 * @param {string} params.payload.id - ID de la selección secundaria a actualizar.
 * @param {number} params.payload.groupIndex - Índice del grupo asociado a la acción secundaria.
 * @returns {void} - La función no retorna ningún valor significativo.
 */
export function* setSecondaryActionForUI(params) {
  // Obtiene el estado de `uiNeeds` del store
  const uiNeeds = yield select(uiNeedsStore);
  const { secondaryOptionsObj, mainOptionsObj } = uiNeeds;
  const { actionId, productType, id, groupIndex } = params.payload;

  // Realiza una copia profunda del objeto `secondaryOptionsObj` para evitar mutaciones
  let newSecondaryOptionsObj = JSON.parse(JSON.stringify(secondaryOptionsObj));

  // Busca la selección secundaria previa en `secondaryOptionsSelected`
  const previousSelection = newSecondaryOptionsObj?.secondaryOptionsSelected?.find(
    secondSelect => secondSelect.id === id && secondSelect.groupIndex === groupIndex && secondSelect.productType === productType
  );

  const previousSelectionIndex = previousSelection ? newSecondaryOptionsObj.secondaryOptionsSelected.indexOf(previousSelection) : -1;

  // Actualiza o elimina la selección secundaria basada en el `actionId` proporcionado
  if (actionId !== CQ_ACTION_CODES.NO_ACTION) {
    // Asegura que `secondaryOptionsSelected` esté inicializado
    if (!newSecondaryOptionsObj.secondaryOptionsSelected) {
      newSecondaryOptionsObj.secondaryOptionsSelected = [];
    }

    const newSelection = { actionId, id, groupIndex, productType };

    // Reemplaza la selección secundaria previa si ya existe, de lo contrario, añade la nueva selección
    if (previousSelectionIndex !== -1) {
      newSecondaryOptionsObj.secondaryOptionsSelected[previousSelectionIndex] = newSelection;
    } else {
      newSecondaryOptionsObj.secondaryOptionsSelected.push(newSelection);
    }
  } else if (previousSelectionIndex !== -1) {
    // Elimina la selección secundaria previa si el `actionId` es `NO_ACTION`
    newSecondaryOptionsObj.secondaryOptionsSelected.splice(previousSelectionIndex, 1);
  }

  // Si la selección previa era una acción de migración, actualiza las opciones secundarias
  if (previousSelection?.actionId === CQ_ACTION_CODES.MIGRATION && previousSelection?.phoneNumber) {
    newSecondaryOptionsObj = addMigrationOptionToElseOptions(
      newSecondaryOptionsObj, 
      productType, 
      groupIndex, 
      previousSelection, 
      mainOptionsObj.mainOptionSelected
    );
  }

  // Actualiza la tienda de `uiNeeds` con la nueva información de las opciones secundarias
  yield put(uiNeedsActions.saveSecondaryActionInfoForUI(newSecondaryOptionsObj));
}



/**
 * Función generadora que actualiza la información de una acción secundaria para la API basada en los parámetros proporcionados.
 * 
 * Esta función realiza las siguientes acciones:
 * 1. Obtiene el estado de la tienda `apisNeeds` del store.
 * 2. Extrae los objetos de necesidades (`selectElementsNeeds`) y opciones secundarias (`selectElementsSecondaryOptions`) del estado.
 * 3. Crea una copia profunda del objeto de necesidades para evitar mutaciones directas.
 * 4. Busca la selección secundaria previa en el objeto de necesidades.
 * 5. Verifica si la acción secundaria es válida comparando el `actionId` y otros criterios.
 * 6. Si la acción es válida, busca el `cqsiId` asociado a la acción secundaria y actualiza la selección secundaria en el objeto de necesidades.
 * 7. Si la acción es `CQ_ACTION_CODES.NO_ACTION`, crea una nueva selección secundaria con un array vacío para `customerQuoteItems` y actualiza el objeto de necesidades.
 * 8. Guarda el nuevo objeto de necesidades en la tienda de `apisNeeds`.
 * 
 * @param {Object} params - Parámetros de entrada para actualizar la acción secundaria para la API.
 * @param {Object} params.payload - Datos necesarios para actualizar la acción secundaria.
 * @param {string} params.payload.actionId - ID de la acción secundaria a configurar.
 * @param {string} params.payload.productType - Tipo de producto asociado a la acción.
 * @param {string} params.payload.id - ID de la selección secundaria a actualizar.
 * @param {number} params.payload.groupIndex - Índice del grupo asociado a la acción secundaria.
 * @param {string} params.payload.description - Descripción asociada a la selección secundaria.
 * @param {string} params.payload.productOfferingName - Nombre de la oferta de producto asociada a la selección secundaria.
 * @returns {void} - La función no retorna ningún valor significativo.
 */
export function* setSecondaryActionForApi(params) {
  // Obtiene el estado de `apisNeeds` del store
  const apisNeeds = yield select(apisNeedsStore);
  const { selectElementsNeeds, selectElementsSecondaryOptions } = apisNeeds;
  const { actionId, productType, id, groupIndex, description, productOfferingName } = params.payload;

  // Realiza una copia profunda del objeto `selectElementsNeeds` para evitar mutaciones
  let newSelectElementsNeeds = JSON.parse(JSON.stringify(selectElementsNeeds));

  // Busca la selección secundaria previa en `secondarySelection`
  const previousSelection = selectElementsNeeds?.secondarySelection?.find(
    secondSelect => secondSelect.id === id && secondSelect.groupIndex === groupIndex && secondSelect.productType === productType
  );

  const previousSelectionIndex = previousSelection ? selectElementsNeeds.secondarySelection.indexOf(previousSelection) : -1;

  // Verifica si la acción secundaria es válida
  const isActionValid = actionId !== CQ_ACTION_CODES.NO_ACTION &&
    selectElementsNeeds?.actionId &&
    selectElementsSecondaryOptions?.[productType]?.parentActionId === selectElementsNeeds.actionId;

  if (isActionValid) {
    // Busca el `cqsiId` asociado a la acción secundaria
    const cqsiId = searchSecondaryClientActionCqsiId(actionId, null, selectElementsSecondaryOptions[productType].choiceGroups, id);

    // Si se encuentra un `cqsiId`, actualiza la selección secundaria
    if (cqsiId) {
      const newSelection = {
        customerQuoteItems: [{ clientPhoneNumberToBeAppliedTo: null, cqsiId }],
        id, groupIndex, productType, actionId, description, productOfferingName, selectedByClient: true,
      };

      // Reemplaza la selección secundaria previa si ya existe, de lo contrario, añade la nueva selección
      if (previousSelectionIndex !== -1) {
        newSelectElementsNeeds.secondarySelection[previousSelectionIndex] = newSelection;
      } else {
        if (!newSelectElementsNeeds.secondarySelection) {
          newSelectElementsNeeds.secondarySelection = [];
        }
        newSelectElementsNeeds.secondarySelection.push(newSelection);
      }

      // Guarda la información actualizada en la tienda de `apisNeeds`
      yield put(apisNeedsActions.saveActionInfoForApi(newSelectElementsNeeds));
    }
  } else if (actionId === CQ_ACTION_CODES.NO_ACTION) {
    // Si la acción es `NO_ACTION`, crea una nueva selección secundaria con un array vacío para `customerQuoteItems`
    const newSelection = {
      actionId, id, groupIndex, productType, description, productOfferingName,
      customerQuoteItems: [], selectedByClient: true,
    };

    // Asegura que `secondarySelection` esté inicializado
    if (!newSelectElementsNeeds.secondarySelection) {
      newSelectElementsNeeds.secondarySelection = [];
    }

    // Reemplaza la selección secundaria previa si ya existe, de lo contrario, añade la nueva selección
    if (previousSelectionIndex !== -1) {
      newSelectElementsNeeds.secondarySelection[previousSelectionIndex] = newSelection;
    } else {
      newSelectElementsNeeds.secondarySelection.push(newSelection);
    }

    // Guarda la información actualizada en la tienda de `apisNeeds`
    yield put(apisNeedsActions.saveActionInfoForApi(newSelectElementsNeeds));
  }
}



/**
 * Función generadora que actualiza la acción secundaria tanto en la interfaz de usuario como en la API.
 * 
 * Esta función realiza las siguientes acciones:
 * 1. Llama a la función `setSecondaryActionForUI` para actualizar la información de la acción secundaria en la interfaz de usuario.
 * 2. Llama a la función `setSecondaryActionForApi` para actualizar la información de la acción secundaria en la API.
 * 
 * @param {Object} params - Parámetros de entrada para configurar la acción secundaria.
 * @param {Object} params.payload - Datos necesarios para configurar la acción secundaria.
 * @param {string} params.payload.actionId - ID de la acción secundaria a configurar.
 * @param {string} params.payload.productType - Tipo de producto asociado a la acción.
 * @param {string} params.payload.id - ID de la selección secundaria a actualizar.
 * @param {number} params.payload.groupIndex - Índice del grupo asociado a la acción secundaria.
 * @param {string} params.payload.description - Descripción asociada a la selección secundaria.
 * @param {string} params.payload.productOfferingName - Nombre de la oferta de producto asociada a la selección secundaria.
 * @returns {void} - La función no retorna ningún valor significativo.
 */
export function* setSecondaryAction(params) {
  // Llama a la función para actualizar la información de la acción secundaria en la interfaz de usuario
  yield call(setSecondaryActionForUI, params);
  
  // Llama a la función para actualizar la información de la acción secundaria en la API
  yield call(setSecondaryActionForApi, params);
}


/**
 * Función generadora que actualiza la información adicional de una acción secundaria en la interfaz de usuario.
 * 
 * Esta función realiza las siguientes acciones:
 * 1. Obtiene el estado de la tienda `uiNeeds` del store.
 * 2. Extrae el objeto de opciones secundarias (`secondaryOptionsObj`) y el objeto de opciones principales (`mainOptionsObj`) del estado.
 * 3. Crea una copia profunda del objeto de opciones secundarias para evitar mutaciones directas.
 * 4. Busca la selección secundaria previa basada en los parámetros proporcionados.
 * 5. Si se encuentra una selección previa, actualiza la selección con el nuevo número de teléfono.
 * 6. Si la acción asociada es `MIGRATION` o `KEEP`, actualiza las opciones de migración en el objeto de opciones secundarias:
 *    - Elimina las opciones de migración existentes asociadas al número de teléfono.
 *    - Si la selección previa tenía un número de teléfono, agrega las opciones de migración actualizadas.
 * 7. Guarda la nueva información de la acción secundaria en la tienda de `uiNeeds`.
 * 
 * @param {Object} params - Parámetros de entrada para actualizar la información adicional de la acción secundaria en la interfaz de usuario.
 * @param {Object} params.payload - Datos necesarios para actualizar la acción secundaria.
 * @param {number} params.payload.groupIndex - Índice del grupo asociado a la acción secundaria.
 * @param {string} params.payload.id - ID de la selección secundaria a actualizar.
 * @param {string} params.payload.phoneNumber - Número de teléfono a asociar con la selección secundaria.
 * @param {string} params.payload.productType - Tipo de producto asociado a la acción.
 * @returns {void} - La función no retorna ningún valor significativo.
 */
export function* setSecondaryActionAdditionalInfoForUI(params) {
  // Desestructura los parámetros de entrada
  const { groupIndex, id, phoneNumber, productType, iccNumber } = params.payload;
  
  // Obtiene el estado de `uiNeeds` del store
  const uiNeeds = yield select(uiNeedsStore);
  const { secondaryOptionsObj, mainOptionsObj } = uiNeeds;

  // Realiza una copia profunda del objeto `secondaryOptionsObj` para evitar mutaciones
  let newSecondaryOptionsObj = JSON.parse(JSON.stringify(secondaryOptionsObj));

  // Busca la selección secundaria previa en `secondaryOptionsSelected`
  const previousSelection = newSecondaryOptionsObj.secondaryOptionsSelected.find(
    secondSelect => secondSelect.id === id && secondSelect.groupIndex === groupIndex && secondSelect.productType === productType
  );

  if (previousSelection) {
    // Actualiza la selección previa con el nuevo número de teléfono
    const newSelection = { ...previousSelection, phoneNumber, iccNumber };
    const previousSelectionIndex = newSecondaryOptionsObj.secondaryOptionsSelected.indexOf(previousSelection);
    newSecondaryOptionsObj.secondaryOptionsSelected[previousSelectionIndex] = newSelection;

    // Si la acción asociada es MIGRATION o KEEP, actualiza las opciones de migración
    if ([CQ_ACTION_CODES.MIGRATION, CQ_ACTION_CODES.KEEP].includes(previousSelection.actionId)) {
      // Elimina las opciones de migración asociadas al número de teléfono
      newSecondaryOptionsObj = removeMigrationOptionsSelected(
        newSecondaryOptionsObj,
        productType,
        groupIndex,
        phoneNumber,
        mainOptionsObj.mainOptionSelected
      );

      // Si la selección previa tenía un número de teléfono, agrega las opciones de migración actualizadas
      if (previousSelection.phoneNumber && previousSelection.phoneNumber && previousSelection.phoneNumber !== phoneNumber) {
        newSecondaryOptionsObj = addMigrationOptionToElseOptions(
          newSecondaryOptionsObj,
          productType,
          groupIndex,
          previousSelection,
          mainOptionsObj.mainOptionSelected
        );
      }
    }
  }

  // Guarda la información actualizada de la acción secundaria en la tienda de `uiNeeds`
  yield put(uiNeedsActions.saveSecondaryActionInfoForUI(newSecondaryOptionsObj));
}



/**
 * Función generadora que actualiza la información adicional de una acción secundaria para la API.
 * 
 * Esta función realiza las siguientes acciones:
 * 1. Obtiene el estado del store `apisNeeds`.
 * 2. Extrae los objetos `selectElementsNeeds` y `selectElementsSecondaryOptions` del estado.
 * 3. Crea una copia profunda del objeto `selectElementsNeeds` para evitar mutaciones directas.
 * 4. Verifica si hay una acción secundaria válida y si hay selecciones secundarias en el estado.
 * 5. Busca la selección secundaria previa basada en los parámetros proporcionados.
 * 6. Si se encuentra una selección previa:
 *    - Actualiza el objeto `customerQuoteItems` con el nuevo `cqsiId`, `clientPhoneNumberToBeAppliedTo`, y `iccNumber` (si está disponible).
 *    - Ajusta otras selecciones secundarias relacionadas:
 *      - Elimina el número de teléfono de las selecciones secundarias en el mismo grupo.
 *      - Si hay una selección pendiente de migración o conservación, asigna el número de teléfono correspondiente basado en las opciones disponibles.
 * 7. Guarda la información actualizada en la API utilizando `apisNeedsActions.saveActionInfoForApi`.
 * 
 * @param {Object} params - Parámetros de entrada para actualizar la información adicional de la acción secundaria en la API.
 * @param {Object} params.payload - Datos necesarios para actualizar la acción secundaria.
 * @param {number} params.payload.groupIndex - Índice del grupo asociado a la acción secundaria.
 * @param {string} params.payload.id - ID de la selección secundaria a actualizar.
 * @param {string} params.payload.phoneNumber - Número de teléfono a asociar con la selección secundaria.
 * @param {string} params.payload.iccNumber - Número ICC asociado con la selección secundaria (opcional).
 * @param {string} params.payload.productType - Tipo de producto asociado a la acción.
 * @returns {void} - La función no retorna ningún valor significativo.
 */
export function* setSecondaryActionAdditionalInfoForApi(params) {
  // Desestructura los parámetros de entrada
  const { groupIndex, id, phoneNumber, iccNumber, productType } = params.payload;

  // Obtiene el estado de `apisNeeds` del store
  const apisNeeds = yield select(apisNeedsStore);
  const { selectElementsNeeds, selectElementsSecondaryOptions } = apisNeeds;

  // Realiza una copia profunda del objeto `selectElementsNeeds` para evitar mutaciones
  let newSelectElementsNeeds = JSON.parse(JSON.stringify(selectElementsNeeds));

  // Verifica si hay una acción secundaria válida y si hay selecciones secundarias
  if (newSelectElementsNeeds?.actionId && newSelectElementsNeeds?.secondarySelection?.length > 0) {
    // Busca la selección secundaria previa en `secondarySelection`
    const secondarySelection = newSelectElementsNeeds.secondarySelection.find(
      secondSelect =>
        secondSelect.id === id &&
        secondSelect.groupIndex === groupIndex &&
        secondSelect.productType === productType
    );

    if (secondarySelection) {
      // Encuentra el índice de la selección secundaria previa
      const secondarySelectionIndex = newSelectElementsNeeds.secondarySelection.indexOf(secondarySelection);

      if (secondarySelectionIndex !== -1 && secondarySelection?.customerQuoteItems?.[0]) {
        // Busca el `cqsiId` asociado con la acción secundaria
        const cqsiId = searchSecondaryClientActionCqsiId(
          secondarySelection.actionId,
          phoneNumber,
          selectElementsSecondaryOptions[productType].choiceGroups,
          secondarySelection.id
        );

        // Actualiza el objeto `customerQuoteItems` con la nueva información
        Object.assign(secondarySelection.customerQuoteItems[0], {
          cqsiId,
          clientPhoneNumberToBeAppliedTo: phoneNumber,
          ...(iccNumber && { iccNumber })
        });

        // Elimina el número de teléfono de otras selecciones en el mismo grupo
        const lineWithSameNumber = newSelectElementsNeeds.secondarySelection.filter(
          secSel =>
            secSel.groupIndex !== groupIndex &&
            secSel.productType === productType &&
            secSel.customerQuoteItems?.[0]?.clientPhoneNumberToBeAppliedTo === phoneNumber
        );

        if (lineWithSameNumber.length === 1) {
          newSelectElementsNeeds.secondarySelection[lineWithSameNumber[0].groupIndex].customerQuoteItems[0].clientPhoneNumberToBeAppliedTo = null;
        }

        // Maneja las selecciones secundarias pendientes
        const secondaryPendingSelections = newSelectElementsNeeds.secondarySelection.filter(
          secSel =>
            secSel.productType === productType &&
            [CQ_ACTION_CODES.MIGRATION, CQ_ACTION_CODES.KEEP].includes(secSel.actionId) &&
            secSel.customerQuoteItems?.[0]?.clientPhoneNumberToBeAppliedTo == null
        );

        if (secondaryPendingSelections.length === 1) {
          const pendingSelectionIndex = secondaryPendingSelections[0].groupIndex;
          const secondaryMadeSelections = newSelectElementsNeeds.secondarySelection.filter(
            secSel =>
              secSel.productType === productType &&
              secSel.customerQuoteItems?.[0]?.clientPhoneNumberToBeAppliedTo != null
          );

          const secondaryMadeSelectionsNumbers = secondaryMadeSelections.map(
            secSel => secSel.customerQuoteItems[0].clientPhoneNumberToBeAppliedTo
          );

          const choiceGroup = selectElementsSecondaryOptions?.[productType]?.choiceGroups?.[pendingSelectionIndex];
          if (choiceGroup) {
            const newChoiceGroup = JSON.parse(JSON.stringify(choiceGroup));
            const filteredCustomerQuote = newChoiceGroup.customerQuoteItems.filter(
              customerQuoteItem =>
                [CQ_ACTION_CODES.MIGRATION, CQ_ACTION_CODES.KEEP].includes(customerQuoteItem.actionId) &&
                !secondaryMadeSelectionsNumbers.includes(customerQuoteItem.clientPhoneNumberToBeAppliedTo)
            );

            if (filteredCustomerQuote.length === 1) {
              secondaryPendingSelections[0].customerQuoteItems[0] = {
                clientPhoneNumberToBeAppliedTo: filteredCustomerQuote[0].clientPhoneNumberToBeAppliedTo,
                cqsiId: filteredCustomerQuote[0].cqsiId
              };
            }
          }
        }
      }

      // Actualiza la selección secundaria en el estado
      newSelectElementsNeeds.secondarySelection[secondarySelectionIndex] = secondarySelection;
    }
  }

  // Guarda la información actualizada en la API
  yield put(apisNeedsActions.saveActionInfoForApi(newSelectElementsNeeds));
}



/**
 * Función generadora que actualiza la información adicional de una acción secundaria tanto en la UI como en la API.
 * 
 * Esta función se encarga de:
 * 1. Llamar a la función `setSecondaryActionAdditionalInfoForUI` para actualizar la información adicional de la acción secundaria en el estado de la UI.
 * 2. Llamar a la función `setSecondaryActionAdditionalInfoForApi` para actualizar la información adicional de la acción secundaria en el estado que se envía a la API.
 * 
 * @param {Object} params - Parámetros necesarios para actualizar la información adicional de la acción secundaria.
 * @param {Object} params.payload - Datos necesarios para actualizar la información adicional de la acción secundaria.
 * @param {number} params.payload.groupIndex - Índice del grupo asociado a la acción secundaria.
 * @param {string} params.payload.id - ID de la selección secundaria a actualizar.
 * @param {string} params.payload.phoneNumber - Número de teléfono a asociar con la selección secundaria.
 * @param {string} params.payload.iccNumber - Número ICC asociado con la selección secundaria (opcional).
 * @param {string} params.payload.productType - Tipo de producto asociado a la acción.
 * @returns {void} - La función no retorna ningún valor significativo.
 */
export function* setSecondaryActionAdditionalInfo(params) {
  // Llama a la función para actualizar la información adicional de la acción secundaria en la UI
  yield call(setSecondaryActionAdditionalInfoForUI, params);
  
  // Llama a la función para actualizar la información adicional de la acción secundaria en la API
  yield call(setSecondaryActionAdditionalInfoForApi, params);
}

